import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordCard() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const isGoogle = user?.auth_provider === "google";

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(data.message || "Password updated.");
      setForm({ current_password: "", new_password: "", confirm: "" });
      setShow({ current: false, next: false, confirm: false });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (isGoogle) {
    return (
      <div className="ser-card p-6" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-4 h-4" style={{ color: "var(--ser-primary)" }} />
          <h3 className="font-serif-display text-2xl" style={{ color: "var(--ser-primary-dark)" }}>
            Password
          </h3>
        </div>
        <p className="text-sm" style={{ color: "var(--ser-text-muted)" }}>
          You signed in with Google, so there is no password on this account. Manage your password through your Google account settings.
        </p>
      </div>
    );
  }

  const renderField = (key, label, showKey) => (
    <div>
      <label className="ser-label">{label}</label>
      <div className="relative">
        <input
          required
          type={show[showKey] ? "text" : "password"}
          minLength={key === "current_password" ? 1 : 6}
          autoComplete={key === "current_password" ? "current-password" : "new-password"}
          value={form[key] ?? form.confirm}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="ser-input"
          style={{ paddingRight: "2.75rem" }}
          data-testid={`pw-${showKey}-input`}
        />
        <button
          type="button"
          onClick={() => setShow({ ...show, [showKey]: !show[showKey] })}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
          style={{ color: "var(--ser-text-muted)" }}
          aria-label={show[showKey] ? "Hide password" : "Show password"}
          data-testid={`pw-${showKey}-toggle`}
        >
          {show[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="ser-card p-6 space-y-4" data-testid="change-password-card">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4" style={{ color: "var(--ser-primary)" }} />
        <h3 className="font-serif-display text-2xl" style={{ color: "var(--ser-primary-dark)" }}>
          Change password
        </h3>
      </div>
      <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>
        Use at least 6 characters. We recommend a mix of letters, numbers and symbols.
      </p>

      <div>
        <label className="ser-label">Current password</label>
        <div className="relative">
          <input
            required
            type={show.current ? "text" : "password"}
            autoComplete="current-password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="ser-input"
            style={{ paddingRight: "2.75rem" }}
            data-testid="pw-current-input"
          />
          <button
            type="button"
            onClick={() => setShow({ ...show, current: !show.current })}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: "var(--ser-text-muted)" }}
            aria-label={show.current ? "Hide" : "Show"}
            data-testid="pw-current-toggle"
          >
            {show.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="ser-label">New password</label>
        <div className="relative">
          <input
            required
            minLength={6}
            type={show.next ? "text" : "password"}
            autoComplete="new-password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="ser-input"
            style={{ paddingRight: "2.75rem" }}
            data-testid="pw-new-input"
          />
          <button
            type="button"
            onClick={() => setShow({ ...show, next: !show.next })}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: "var(--ser-text-muted)" }}
            aria-label={show.next ? "Hide" : "Show"}
            data-testid="pw-new-toggle"
          >
            {show.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="ser-label">Confirm new password</label>
        <div className="relative">
          <input
            required
            minLength={6}
            type={show.confirm ? "text" : "password"}
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="ser-input"
            style={{ paddingRight: "2.75rem" }}
            data-testid="pw-confirm-input"
          />
          <button
            type="button"
            onClick={() => setShow({ ...show, confirm: !show.confirm })}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: "var(--ser-text-muted)" }}
            aria-label={show.confirm ? "Hide" : "Show"}
            data-testid="pw-confirm-toggle"
          >
            {show.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <button type="submit" disabled={saving} className="btn-ser-primary w-full" data-testid="pw-submit-btn">
        <KeyRound className="w-4 h-4" />
        {saving ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}
